import Link from 'next/link';
import Pager from 'rc-pagination';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import BookingPage, {
  Pagination,
} from '../../../../domain/entities/BookingPage';
import { CategoryEntity } from '../../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../../interfaces/merchants';
import Button, { ButtonSize } from '../../../../atoms/buttons/Button';
import Heading from '../../../../atoms/Heading';
import BaseTemplate from '../../../../organisms/merchants/BaseTemplate';
import BookingPageCard from '../../../../molecules/merchant/common/cards/BookingPageCard';
import MerchantProfile from '../../../../molecules/merchant/MerchantProfile';
import { useLineLiffAccessToken } from '../../../../hooks/line-liff';
import LineMiniAppButtonLink from '../../../../molecules/line/LineMiniAppButtonLink';
import css from './MerchantBookingServicesPage.module.scss';

export interface PageProps {
  merchantDetail: MerchantDetail;
  public_id: string;
  category: CategoryEntity;
  bookingPages: BookingPage[];
  pagination: Pagination;
  isCoubicApp: boolean;
}

const MerchantBookingServicesPage: React.FC<PageProps> = ({
  bookingPages,
  merchantDetail,
  pagination: { totalSize, currentPage },
  isCoubicApp,
}) => {
  const intl = useIntl();
  const handlePageChange = useCallback((page: number) => {
    location.href = `${location.pathname}?page=${page}#pageContent`;
  }, []);

  const { lineAccessToken, isReady } = useLineLiffAccessToken(
    merchantDetail.line_liff_id,
  );
  const isNotFromLineMiniApp = lineAccessToken === null;

  return (
    <div className={css.MerchantBookingServicesPage}>
      <BaseTemplate merchantDetail={merchantDetail} isCoubicApp={isCoubicApp}>
        <div className={css.MerchantBookingServicesPage__Content}>
          {merchantDetail.line_liff_id && isReady && isNotFromLineMiniApp && (
            <div className={css.line_mini_app_link}>
              <LineMiniAppButtonLink
                liffId={merchantDetail.line_liff_id}
                path="/booking_pages"
                hash="#pageContent"
              />
              <p className={css.line_mini_app_description}>
                {intl.formatMessage({
                  id: 'merchants.details.canReceiveMessageFromLINE',
                })}
              </p>
            </div>
          )}
          <Heading className={css.MerchantBookingServicesPage__Heading}>
            <Link
              href={`/${merchantDetail.public_id}/booking_pages#pageContent`}
              prefetch={false}
              className={css.MerchantBookingServicesPage__Link}
            >
              {intl.formatMessage({
                id: 'merchants.bookingPages.index.title',
              })}
            </Link>
            {currentPage > 1 &&
              ` ${intl.formatMessage(
                { id: 'merchants.bookingPages.index.page' },
                { currentPage },
              )}`}
          </Heading>
          {bookingPages.length > 0 && (
            <div>
              {bookingPages.map((bookingPage) => (
                <BookingPageCard
                  key={bookingPage.publicId}
                  className={css.MerchantBookingServicesPage__BookingPageCard}
                  title={bookingPage.name}
                  description={bookingPage.description.text || ''}
                  url={`/${merchantDetail.public_id}/${bookingPage.publicId}#pageContent`}
                  thumbnailUrl={bookingPage.thumbnailUrl}
                  tags={bookingPage.tagNames}
                  lowestPrice={bookingPage.lowestPrice}
                  uniquePricing={bookingPage.uniquePricing}
                />
              ))}
            </div>
          )}
          <div className={css.MerchantBookingServicesPage__Pager}>
            <Pager
              onChange={handlePageChange}
              current={currentPage}
              total={totalSize}
              showTitle={false}
            />
          </div>
          <div
            className={css.MerchantBookingServicesPage__MerchantProfileSection}
          >
            <Heading className={css.MerchantBookingServicesPage__Heading}>
              {intl.formatMessage(
                { id: 'merchants.details.storeData' },
                { displayName: merchantDetail.display_name },
              )}
            </Heading>
            <MerchantProfile merchantDetail={merchantDetail} />
            <div
              className={
                css.MerchantBookingServicesPage__BackToTopButtonSection
              }
            >
              <Button
                className_Deprecated={
                  css.MerchantBookingServicesPage__BackToTopButton
                }
                isGhost={true}
                isFull={true}
                href={`/${merchantDetail.public_id}`}
                buttonSize={ButtonSize.Medium}
              >
                {intl.formatMessage(
                  {
                    id: 'merchants.details.backToMerchantTop',
                  },
                  { displayName: merchantDetail.display_name },
                )}
              </Button>
            </div>
          </div>
        </div>
      </BaseTemplate>
    </div>
  );
};

export default MerchantBookingServicesPage;
